/**
 * Materials Tab Component
 * Features: CSV/PDF export, configurable row count, contextual empty state
 */

import { useState } from "react";
import {
  BarChart, Bar, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from "recharts";
import { Package } from "lucide-react";
import { 
  ChartCard, TableCard, CustomTooltip, DataTable, 
  TableRow, TableCell, EmptyState, EMPTY_STATES 
} from "./shared";
import {
  CATEGORY_COLORS, CHART_STYLE, formatCurrency, formatCompactCurrency,
  formatNumber, formatMonthLabel, exportToCSV, DEFAULT_ROW_COUNT,
  generateAnalyticsPdfContent, generateTableHtml,
} from "../../lib/chartUtils";
import useExportDialog from "../../hooks/useExportDialog";

const MaterialsTab = ({ data }) => {
  const [rowCount, setRowCount] = useState(DEFAULT_ROW_COUNT);
  const { exportToPdf } = useExportDialog();

  if (!data) {
    return (
      <EmptyState 
        icon={Package}
        {...EMPTY_STATES.materials}
      />
    );
  }

  const { by_material = [], by_category = [], trends = [] } = data;

  if (by_material.length === 0 && by_category.length === 0) {
    return (
      <EmptyState 
        icon={Package}
        {...EMPTY_STATES.materials}
      />
    );
  }

  const handleExportCSV = () => {
    exportToCSV(by_material, "material-usage",
      ["Material", "Category", "Quantity", "Total Cost"],
      {
        "Material": "name",
        "Category": "category",
        "Quantity": "quantity",
        "Total Cost": "cost",
      }
    );
  };

  const handleExportPDF = () => {
    const tableRows = by_material.slice(0, rowCount).map(mat => [
      mat.name,
      mat.category || "Other",
      formatNumber(mat.quantity),
      formatCurrency(mat.cost),
    ]);

    const tableHtml = generateTableHtml(
      ["Material", "Category", "Quantity", "Total Cost"],
      tableRows,
      ["left", "left", "right", "right"]
    );

    const totalCost = by_material.reduce((sum, mat) => sum + mat.cost, 0);
    
    const categoryHtml = by_category.length > 0 ? `
      <div style="display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 16px;">
        ${by_category.map(cat => `
          <div style="background: #f5f5f5; padding: 8px 16px; border-radius: 4px;">
            <strong>${cat.category}:</strong> ${formatCurrency(cat.cost)}
          </div>
        `).join("")}
      </div>
    ` : "";

    const summaryHtml = `
      <div style="margin-bottom: 16px;">
        <strong>Total Material Cost:</strong> ${formatCurrency(totalCost)}
      </div>
      ${categoryHtml}
    `;

    const content = generateAnalyticsPdfContent("Material Usage Report", [
      { title: "Summary by Category", content: summaryHtml },
      { title: "Material Details", content: tableHtml },
    ]);

    exportToPdf("Material Usage Report", content);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Pie Chart */}
        <ChartCard title="Cost by Category">
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={by_category}
                dataKey="cost"
                nameKey="category"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                labelLine={false}
              >
                {by_category.map((entry, index) => (
                  <Cell key={entry.category} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={formatCurrency} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Top Materials Bar Chart */}
        <ChartCard title="Top Materials by Cost">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={by_material.slice(0, 8)} layout="vertical" margin={{ top: 20, right: 40, left: 100, bottom: 20 }}>
              <CartesianGrid {...CHART_STYLE.grid} />
              <XAxis type="number" tickFormatter={formatCompactCurrency} tick={CHART_STYLE.axis} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={90} />
              <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
              <Bar dataKey="cost" name="Cost" fill="#14b8a6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Material Trends */}
      {trends.length > 0 && (
        <ChartCard title="Material Cost Trends" className="">
          <ResponsiveContainer width="100%" height={256}>
            <AreaChart data={trends} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
              <CartesianGrid {...CHART_STYLE.grid} />
              <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={CHART_STYLE.axis} />
              <YAxis tickFormatter={formatCompactCurrency} tick={CHART_STYLE.axis} />
              <Tooltip content={<CustomTooltip formatter={formatCurrency} />} labelFormatter={formatMonthLabel} />
              <Area type="monotone" dataKey="Brick" stackId="1" stroke={CATEGORY_COLORS[0]} fill={CATEGORY_COLORS[0]} fillOpacity={0.6} />
              <Area type="monotone" dataKey="Mortar" stackId="1" stroke={CATEGORY_COLORS[1]} fill={CATEGORY_COLORS[1]} fillOpacity={0.6} />
              <Area type="monotone" dataKey="Other" stackId="1" stroke={CATEGORY_COLORS[2]} fill={CATEGORY_COLORS[2]} fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Materials Table */}
      <TableCard 
        title="Material Usage Details"
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
        rowCount={rowCount}
        onRowCountChange={setRowCount}
      >
        <DataTable headers={[
          { label: "Material" },
          { label: "Category" },
          { label: "Quantity Used", align: "right" },
          { label: "Total Cost", align: "right" },
        ]}>
          {by_material.slice(0, rowCount).map((mat) => (
            <TableRow key={`${mat.name}-${mat.category || "Other"}`} className="table-row">
              <TableCell className="font-medium">{mat.name}</TableCell>
              <TableCell>{mat.category || "Other"}</TableCell>
              <TableCell className="text-right">{formatNumber(mat.quantity)}</TableCell>
              <TableCell className="text-right">{formatCurrency(mat.cost)}</TableCell>
            </TableRow>
          ))}
        </DataTable>
      </TableCard>
    </div>
  );
};

export default MaterialsTab;
